import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Check, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { STATUS_LABELS, STATUS_STYLES } from "@/lib/connectionsCatalog";

const StatusBadge = ({ status, testId }) => (
  <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${STATUS_STYLES[status]}`} data-testid={testId}>
    {STATUS_LABELS[status]}
  </span>
);

const WizardField = ({ field, value, onChange }) => {
  if (field.type === "info") {
    return <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500 dark:bg-white/5 dark:text-slate-400" data-testid={`org-connections-field-${field.key}`}>{field.text}</p>;
  }
  if (field.type === "toggle") {
    return (
      <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 dark:border-white/10" data-testid={`org-connections-field-${field.key}`}>
        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{field.label}</span>
        <button
          type="button"
          onClick={() => onChange(!value)}
          data-testid={`org-connections-field-${field.key}-toggle`}
          className={`relative h-5 w-9 rounded-full transition-colors duration-200 ${value ? "bg-[#0052FF]" : "bg-slate-200 dark:bg-white/15"}`}
        >
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${value ? "translate-x-4" : "translate-x-0.5"}`} />
        </button>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <label className="mb-1 block text-xs font-bold text-slate-500 dark:text-slate-400">{field.label}</label>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`org-connections-field-${field.key}`}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 dark:border-white/15 dark:bg-[#0B1220] dark:text-white"
        >
          <option value="">Choose…</option>
          {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div>
        <label className="mb-1 block text-xs font-bold text-slate-500 dark:text-slate-400">{field.label}</label>
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={2}
          data-testid={`org-connections-field-${field.key}`}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-white/15 dark:bg-[#0B1220] dark:text-white"
        />
      </div>
    );
  }
  return (
    <div>
      <label className="mb-1 block text-xs font-bold text-slate-500 dark:text-slate-400">{field.label}{field.required && " *"}</label>
      <div className="flex items-center gap-2">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          data-testid={`org-connections-field-${field.key}`}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-white/15 dark:bg-[#0B1220] dark:text-white"
        />
        {field.suffix && <span className="shrink-0 text-xs font-semibold text-slate-400">{field.suffix}</span>}
      </div>
    </div>
  );
};

export const SetupDrawer = ({ capability, statusMap, onClose, onConnectionSaved }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState({ screen: "dashboard" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formValues, setFormValues] = useState({});
  const [savedResult, setSavedResult] = useState(null);

  const subMap = statusMap[capability.key] || {};

  const patchConnection = async (sub, payload) => {
    const { data } = await axios.patch(`${API}/org/connections`, {
      capability_key: capability.key,
      sub_capability_key: sub.key,
      ...payload,
    }, { withCredentials: true });
    onConnectionSaved(capability.key, sub.key, data.connection);
    return data.connection;
  };

  const openSub = (sub) => {
    setError("");
    setSavedResult(null);
    setView({ screen: "methods", subKey: sub.key });
  };

  const chooseMethod = async (sub, method) => {
    setError("");
    if (method.kind === "navigate") {
      onClose();
      navigate(method.path);
      return;
    }
    if (method.kind === "skip") {
      setSaving(true);
      try {
        await patchConnection(sub, { method_key: "skip", stage: "final" });
        setView({ screen: "dashboard" });
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save this selection.");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (method.kind === "provider_picker") {
      setView({ screen: "providers", subKey: sub.key, methodKey: method.key });
      return;
    }
    // nova_wizard: mark setup_in_progress, then open the wizard prefilled
    setSaving(true);
    try {
      const current = subMap[sub.key];
      const prefill = current?.method_key === method.key ? current.configuration || {} : {};
      await patchConnection(sub, { method_key: method.key, stage: "draft", configuration: prefill });
      const defaults = {};
      (method.fields || []).forEach((f) => { if (f.type === "toggle") defaults[f.key] = f.default ?? false; });
      setFormValues({ ...defaults, ...prefill });
      setView({ screen: "wizard", subKey: sub.key, methodKey: method.key });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't start this setup.");
    } finally {
      setSaving(false);
    }
  };

  const chooseProvider = async (sub, methodKey, providerKey) => {
    setSaving(true);
    setError("");
    try {
      await patchConnection(sub, { method_key: methodKey, stage: "final", provider_key: providerKey });
      setView({ screen: "dashboard" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save this provider.");
    } finally {
      setSaving(false);
    }
  };

  const submitWizard = async (sub, method) => {
    setSaving(true);
    setError("");
    try {
      const connection = await patchConnection(sub, { method_key: method.key, stage: "final", configuration: formValues });
      setSavedResult(connection);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save this configuration.");
    } finally {
      setSaving(false);
    }
  };

  const renderDashboard = () => (
    <div className="mt-5 space-y-3">
      {capability.subCapabilities.map((sub) => {
        if (sub.comingSoon) {
          return (
            <div
              key={sub.key}
              data-testid={`org-connections-sub-${capability.key}-${sub.key}`}
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 p-4 opacity-60 dark:border-white/10"
            >
              <p className="text-sm font-bold text-slate-500 dark:text-slate-400">{sub.label}</p>
              <span className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-400 dark:border-white/10 dark:text-slate-500">Coming Soon</span>
            </div>
          );
        }
        const current = subMap[sub.key];
        const status = current?.status || "not_configured";
        return (
          <button
            key={sub.key}
            type="button"
            onClick={() => openSub(sub)}
            data-testid={`org-connections-sub-${capability.key}-${sub.key}`}
            className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 p-4 text-left transition-colors duration-200 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5"
          >
            <div>
              <p className="text-sm font-bold text-slate-950 dark:text-white">{sub.label}</p>
              {current?.provider_key && <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">Selected: {sub.methods.find((m) => m.key === current.method_key)?.providers?.find((p) => p.key === current.provider_key)?.label || current.provider_key}</p>}
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={status} testId={`org-connections-status-${capability.key}-${sub.key}`} />
              <ChevronRight size={16} className="text-slate-400" />
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderMethods = () => {
    const sub = capability.subCapabilities.find((s) => s.key === view.subKey);
    const current = subMap[sub.key];
    return (
      <div className="mt-5">
        <button type="button" onClick={() => setView({ screen: "dashboard" })} data-testid="org-connections-back-btn" className="mb-4 inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white">
          <ChevronLeft size={14} /> Back
        </button>
        <p className="text-sm font-bold text-slate-950 dark:text-white">{sub.label}</p>
        <p className="mt-1 text-xs font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">How would you like to set this up?</p>
        <div className="mt-3 flex flex-col gap-2">
          {sub.methods.map((method) => {
            const MIcon = method.icon;
            const isCurrent = current?.method_key === method.key && current?.status !== "not_configured";
            return (
              <button
                key={method.key}
                type="button"
                onClick={() => chooseMethod(sub, method)}
                disabled={saving}
                data-testid={`org-connections-method-${capability.key}-${sub.key}-${method.key}`}
                className={`flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left text-xs font-bold transition-colors duration-200 disabled:opacity-50 ${
                  isCurrent
                    ? "border-[#0052FF] bg-[#0052FF]/10 text-[#0052FF] dark:border-blue-400/60 dark:bg-blue-500/10 dark:text-blue-400"
                    : "border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                }`}
              >
                <MIcon size={14} /> {method.label} {isCurrent && <Check size={12} className="ml-auto" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderProviders = () => {
    const sub = capability.subCapabilities.find((s) => s.key === view.subKey);
    const method = sub.methods.find((m) => m.key === view.methodKey);
    const current = subMap[sub.key];
    return (
      <div className="mt-5">
        <button type="button" onClick={() => setView({ screen: "methods", subKey: sub.key })} data-testid="org-connections-back-btn" className="mb-4 inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white">
          <ChevronLeft size={14} /> Back
        </button>
        <p className="text-sm font-bold text-slate-950 dark:text-white">{method.label}</p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Choose the provider your organization already uses.</p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`org-connections-providers-${capability.key}-${sub.key}`}>
          {method.providers.map((p) => {
            const selected = current?.method_key === "existing" && current?.provider_key === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => chooseProvider(sub, method.key, p.key)}
                disabled={saving}
                data-testid={`org-connections-provider-${capability.key}-${sub.key}-${p.key}`}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors duration-200 disabled:opacity-50 ${
                  selected
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-400"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"
                }`}
              >
                {selected && <Check size={12} />} {p.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWizard = () => {
    const sub = capability.subCapabilities.find((s) => s.key === view.subKey);
    const method = sub.methods.find((m) => m.key === view.methodKey);
    if (savedResult) {
      return (
        <div className="mt-5" data-testid={`org-connections-wizard-success-${capability.key}-${sub.key}`}>
          <p className="text-sm font-bold text-slate-950 dark:text-white">{method.label}</p>
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/30 dark:bg-emerald-500/10">
            <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">
              {savedResult.status === "active" ? "Configured and active." : "Saved. This is on our provisioning list."}
            </p>
            {savedResult.configuration?.form_id && (
              <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">Nova Form ID: {savedResult.configuration.form_id} · Public form page coming soon.</p>
            )}
            {savedResult.configuration?.lifecycle_stages && (
              <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">Lifecycle: {savedResult.configuration.lifecycle_stages.join(" → ")}</p>
            )}
            {savedResult.status === "pending_provisioning" && (
              <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">We'll notify you once this is fully provisioned.</p>
            )}
          </div>
          <button type="button" onClick={() => setView({ screen: "dashboard" })} data-testid="org-connections-wizard-done-btn" className="mt-4 rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0041CC]">
            Done
          </button>
        </div>
      );
    }
    const requiredMissing = (method.fields || []).some((f) => f.required && !(formValues[f.key] || "").toString().trim());
    return (
      <div className="mt-5">
        <button type="button" onClick={() => setView({ screen: "methods", subKey: sub.key })} data-testid="org-connections-back-btn" className="mb-4 inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white">
          <ChevronLeft size={14} /> Back
        </button>
        <p className="text-sm font-bold text-slate-950 dark:text-white">{method.label}</p>
        <StatusBadge status="setup_in_progress" testId={`org-connections-wizard-status-${capability.key}-${sub.key}`} />
        <div className="mt-4 space-y-3" data-testid={`org-connections-wizard-${capability.key}-${sub.key}`}>
          {(method.fields || []).map((field) => (
            <WizardField
              key={field.key}
              field={field}
              value={formValues[field.key]}
              onChange={(v) => setFormValues((prev) => ({ ...prev, [field.key]: v }))}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => submitWizard(sub, method)}
          disabled={saving || requiredMissing}
          data-testid={`org-connections-wizard-save-${capability.key}-${sub.key}`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0041CC] disabled:opacity-50"
        >
          {saving && <Loader2 size={14} className="animate-spin" />} Save
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" data-testid="org-connections-drawer">
      <div className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#0B1220]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">{capability.label}</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{capability.subtitle}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="org-connections-drawer-close-btn" className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10">
            <X size={16} />
          </button>
        </div>

        {error && <p className="mt-3 text-xs font-semibold text-red-600 dark:text-red-400" data-testid="org-connections-drawer-error">{error}</p>}

        {view.screen === "dashboard" && renderDashboard()}
        {view.screen === "methods" && renderMethods()}
        {view.screen === "providers" && renderProviders()}
        {view.screen === "wizard" && renderWizard()}

        {view.screen === "dashboard" && (
          <div className="mt-5 flex justify-end">
            <button type="button" onClick={onClose} data-testid="org-connections-drawer-done-btn" className="rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0041CC]">Done</button>
          </div>
        )}
      </div>
    </div>
  );
};
